import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import css from '../components/common/common.module.css';
import {Icon} from '../components/common/Icon';

type ToastType = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

type ShowToast = (message: string, type?: ToastType) => void;

const ToastContext = createContext<ShowToast>(() => {});

export function ToastProvider({children}: {children: ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const showToast = useCallback<ShowToast>((message, type = 'info') => {
    const id = nextId.current++;
    setToasts(current => [...current, {id, message, type}]);
    setTimeout(() => {
      setToasts(current => current.filter(t => t.id !== id));
    }, 3500);
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className={`${css.toastStack} no-print`}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={
              toast.type === 'error'
                ? css.toastError
                : toast.type === 'success'
                  ? css.toastSuccess
                  : css.toast
            }
          >
            {toast.type === 'success' && <Icon name="check" size={15} />}
            {toast.type === 'error' && <Icon name="alert" size={15} />}
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
